import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { Button, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSaveData } from '../../../hooks/useSaveData';
import { useLoadData } from '../../../hooks/useLoadData';
import { muniMappingcolumns } from './muni-mapping-columns';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { municipalityApiKeyStore } from '../../../api/masters/municipality-api-key-store';
import { serviceMunicipalityMappingsApiKeyStore } from '../../../api/masters/service-municipality-mappings-api-key-store';
import { ApiResponse } from '../../../models/api-response';
import { Service } from '../../../models/masters/service';
import { SaveHookFormDialog } from '../../../components/save-hook-form.dialog';
import { ServiceMunicipalityForm } from './service-municipality.form';
import { ServiceMunicipalityMappingDialog } from './municipality-mapping/municipality-mapping.dialog';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import EditIcon from '@mui/icons-material/Edit';
import { useDeleteData } from '../../../hooks/useDeleteData';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import { useAuthorize } from '../../../hooks/use-authorize';

interface ChangedService {
  id: number;
  commonID: number;
  changes: Record<string, { before: any; after: any }>;
}

interface CellProps {
  service: any;
}

interface CustomGridCellProps {
  field: string;
}

export const ServicesMunicipalityMappingTab = () => {
  const [mappedID, setMappedID] = useState();
  const queryClient = useQueryClient();
  const [openMappingDialog, setOpenMunicipalityMappingDialog] = useState(false);
  const [editItem, setEditItem] = useState<Partial<Service>>();
  const [currentItem, setCurrentItem] = useState<string>('');
  const [currentItemID, setCurrentItemID] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);

  const { data, isLoading } = useLoadData<ApiResponse<any>>(municipalityApiKeyStore.getMunicipalities());
  const { isLoading: isMuniSortLoading, mutateAsync: mutateMuniSortAsync } = useSaveData<any>(servicesApiKeyStore.updateServiceSortOrder());

  const {
    data: serviceMuniData,
    isLoading: isSerMuniLoading,
    isFetching,
    refetch,
  } = useLoadData<ApiResponse<any[]>>(servicesApiKeyStore.getServiceMunicipalityMappings(mappedID));

  const { isLoading: isLoadingSave, mutateAsync } = useSaveData<any>(
    serviceMunicipalityMappingsApiKeyStore.editServiceMunicipality()
  );
  const {
    isLoading: isLoadingDeleteService,
    isError,
    error,
    mutateAsync: deleteService,
  } = useDeleteData(serviceMunicipalityMappingsApiKeyStore.deleteServiceMunicipality(currentItemID));

  const handleSelectChange = (event: any) => {
    setMappedID(event.target.value);
  };

  const handleEditService = (service: Service) => {
    setEditItem(service);
  };

  const handleEditClose = () => {
    setEditItem(undefined);
  };

  const handleOpenMunicipalityMappingDialog = () => {
    setOpenMunicipalityMappingDialog(true);
  };

  const handleCloseMunicipalityMappingDialog = () => {
    setOpenMunicipalityMappingDialog(false);
    queryClient.invalidateQueries(servicesApiKeyStore.getServiceMunicipalityMappings(mappedID).queryKey);
  };

  const formatSubmitdata = (data: any) => {
    return {
      ...data,
      servicePredecessors: [data.servicePredecessors],
      serviceEquipments: data.serviceEquipments?.map((item: any) => item.equipmentID),
      serviceSkillsets: data.serviceSkillsets?.map((item: any) => item.skillsetID),
      userManagers: data.userManagers?.map((item: any) => item.userID),
    };
  };

  const findAndUpdateChangedName = (oldArray: any, newArray: any) => {
    const changedNames: any[] = [];
    Array.isArray(oldArray) &&
      oldArray?.forEach((oldObj: any, index: any) => {
        const newObj = newArray[index];
        if (oldObj.serviceCode !== newObj.serviceCode) {
          changedNames.push(newObj);
        }
      });
    return changedNames;
  };

  const findChangedObjects = (array1: Service[], array2: Service[]): ChangedService[] => {
    const array2Map = new Map(array2.map((obj) => [obj.serviceID, obj]));

    const changedObjects: ChangedService[] = [];

    Array.isArray(array1) &&
      array1?.forEach((obj1) => {
        const obj2 = array2Map.get(obj1.serviceID);

        if (!obj2) {
          changedObjects.push({
            id: obj1.serviceID,
            commonID: obj1?.municipalityID,
            changes: {},
          });
          return;
        }

        const changes: Record<string, { before: any; after: any }> = {};
        Array.isArray(Object.keys(obj1)) &&
          Object.keys(obj1)?.forEach((key) => {
            if (obj1[key] !== obj2[key]) {
              changes[key] = { before: obj1[key], after: obj2[key] };
            }
          });

        if (Object.keys(changes).length > 0) {
          changedObjects.push({
            id: obj1.serviceID,
            commonID: obj1?.municipalityID,
            changes,
          });
        }
      });

    return changedObjects;
  };

  const submitHanlder = async () => {
    await deleteService();
    refetch();
    closeDialog();
  };

  const closeDialog = () => {
    setIsOpen(false);
  };
  const openDialog = (id: any) => {
    setIsOpen(true);
    setCurrentItemID(id);
  };

  const handleOnChange = async (value: any[]) => {
    const updatedData = findAndUpdateChangedName(serviceMuniData?.data, value);
    if (updatedData.length > 0) {
      await mutateAsync({
        data: updatedData[0],
      });
      refetch();
    }
    if (serviceMuniData?.data && value?.length > 0) {
      const changed = findChangedObjects(serviceMuniData?.data, value);
      if (changed?.length > 0) {
        const sortOrderValue = !changed[0]?.changes?.muncipalityServiceSortOrder?.after ? '0' : changed[0]?.changes?.muncipalityServiceSortOrder?.after;
        const submitingData = {
          serviceID: changed[0]?.id,
          municipalityID: changed[0]?.commonID,
          sortOrder: Number(sortOrderValue),
        };
        if (!isReadOnlyPermission) {
          await mutateMuniSortAsync({
            data: submitingData,
          });
          refetch();
        }
      }
    }
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  const EditCell = ({ service }: CellProps) => {
    return (
      <td>
        {/* <GuardedComponent requiredPermissions={[]}> */}
        {!isReadOnlyPermission && (
          <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_SERVICE_MUNICIPALITY_MAPPING]}>
            <IconButton aria-label="edit" onClick={() => setCurrentItem(service?.serviceCode)}>
              <Tooltip title={<Typography variant="body1">Edit</Typography>}>
                <EditIcon className="btnEdit" />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
        )}
        <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_SERVICE]}>
          <IconButton style={{ padding: 5 }} aria-label="delete" onClick={() => openDialog(service?.mappingID)}>
            <Tooltip title={<Typography variant="body1">Delete</Typography>}>
              <DeleteIcon className="btnDelete" />
            </Tooltip>
          </IconButton>
        </GuardedComponent>
        {isOpen && service?.mappingID === currentItemID && (
          <Dialog className="modalpopup" title={'CONFIRM DELETE'} onClose={closeDialog}>
            <div>
              <div style={{ padding: '25px 50px' }}>
                <div>Are you sure you want to permanently delete?</div>
                <LoadingIndicator isLoading={isLoading} />
                {/* <ApiErrorComponent isError={isError} errorRespnse={error} /> */}
              </div>
            </div>
            <DialogActionsBar>
              <div className=" spaceBetween">
                <button className="btn btn-sm btn-default btn-lightGrey" onClick={closeDialog} autoFocus>
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-danger btn-delete"
                  style={{
                    backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
                    color: isReadOnlyPermission ? 'black' : '#fff'
                  }}
                  type="button"
                  onClick={submitHanlder}
                  disabled={isLoading || isReadOnlyPermission}
                >
                  Delete
                </button>
              </div>
            </DialogActionsBar>
          </Dialog>
        )}
      </td>
    );
  };

  const handleOnBlur = async (service: any, serviceCode: string) => {
    // Create a copy of the service object and update the serviceCode
    const updatedService = {
      serviceID: service?.serviceID,
      mappingID: service?.mappingID,
      serviceCode,
      municipalityID: service?.municipalityID,
    };
    await mutateAsync({
      data: updatedService,
    });
    refetch();
    setCurrentItem('');
    // For debugging purposes
    // You can further handle updatedService as needed
  };

  const ServiceCodeCell = ({ service }: CellProps) => {
    const [serviceCode, setServiceCode] = useState<string>(service?.serviceCode);

    const handleOnChange = (e: any) => {
      setServiceCode(e?.target?.value);
    };

    return (
      <td>
        {currentItem === service?.serviceCode ? (
          <TextField
            value={serviceCode}
            onBlur={() => handleOnBlur(service, serviceCode)} // Call handleOnBlur on blur
            onChange={(e: any) => handleOnChange(e)}
            variant="outlined"
            size="small"
          />
        ) : (
          <Typography variant="body1" style={{}}>
            {service?.serviceCode}
          </Typography>
        )}
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'serviceCode') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <ServiceCodeCell service={dataItem.dataItem} />;
      };
    }

    if (field === 'options') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <EditCell service={dataItem.dataItem} />;
      };
    }

    // Return undefined if the field is not 'bluePrint'
    return undefined;
  };

  return (
    <Grid item>
      <LoadingIndicator isLoading={isLoading} />
      {data && (
        <FormControl
          fullWidth
          className="spaceBetween"
          style={{ display: 'flex', flexDirection: 'row', marginBottom: '8px', marginTop: '8px' }}
        >
          <>
            <div>
              <InputLabel size="small" id="demo-simple-select-label">
                Municipality
              </InputLabel>
              <Select
                style={{ width: '200px' }}
                size="small"
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={mappedID}
                label="Mapping Type"
                onChange={handleSelectChange}
              >
                {data &&
                  data.data.map((item: any) => (
                    <MenuItem key={`${item.municipalityCode}-${item.municipalityID}`} value={item.municipalityID}>
                      {item.muncipalityName}
                    </MenuItem>
                  ))}
              </Select>
            </div>
            <GuardedComponent requiredPermissions={[UserPermissionName.ADD_SERVICE_MUNICIPALITY_MAPPING]}>
              <div>
                <Button className="btnLink" type="button" onClick={handleOpenMunicipalityMappingDialog}>
                  Map Services
                </Button>
              </div>
            </GuardedComponent>
          </>
        </FormControl>
      )}
      {data && (
        <SiTrackGrid
          fileTitle='service_municipality'
          dataItems={serviceMuniData?.data}
          dataItemKey="serviceID"
          columns={muniMappingcolumns}
          onGridDataChange={handleOnChange}
          customCell={(field) =>
            CustomGridCell({
              field,
            })
          }
          actionPermissions={{
            export: UserPermissionName.DOWNLOAD_GRID
          }}
          // deleteMutationConfig={(item) =>
          //   serviceMunicipalityMappingsApiKeyStore.deleteServiceMunicipality(item.mappingID)
          // }
          isBusy={isSerMuniLoading || isMuniSortLoading || isFetching || isLoadingSave}
        />
      )}
      {editItem && (
        <SaveHookFormDialog
          title={'UPDATE SERVICE MUNICIPALITY MAPPING'}
          config={{
            saveConfig: serviceMunicipalityMappingsApiKeyStore.editServiceMunicipality(),
            invalidateRoute: () => servicesApiKeyStore.getServiceMunicipalityMappings(mappedID),
          }}
          formatSubmitdata={formatSubmitdata}
          data={editItem}
          onClose={handleEditClose}
          size="md"
        >
          <ServiceMunicipalityForm />
        </SaveHookFormDialog>
      )}
      {openMappingDialog && (
        <ServiceMunicipalityMappingDialog
          municipalityID={mappedID ?? 0}
          onClose={handleCloseMunicipalityMappingDialog}
          open={openMappingDialog}
        />
      )}
    </Grid>
  );
};
